import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../context/AuthProvider'
import { useUserRole } from '../../../context/UserRoleProvider'
import { api, apiRequest } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Loader2, Plus, Users, Lock } from 'lucide-react'

export default function ParishGroups() {
  const { user } = useAuth()
  const { getParishAdminParishId } = useUserRole()
  const navigate = useNavigate()
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [parishId, setParishId] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [creating, setCreating] = useState(false)
  const [formError, setFormError] = useState(null)
  const [form, setForm] = useState({ name: '', description: '' })

  const resolveParishId = async () => {
    let id = getParishAdminParishId()
    if (!id) {
      const userData = await apiRequest('/api/v1/auth/me')
      const roleDetails = userData?.roleDetails || []
      id = roleDetails.find((r) => r.name === 'parish-admin')?.parishId
    }
    return id
  }

  const fetchGroups = async () => {
    try {
      setLoading(true)
      setError(null)

      const id = await resolveParishId()
      if (!id) {
        setError('You are not associated with a parish as a parish admin.')
        setLoading(false)
        return
      }
      setParishId(id)

      const response = await api.getParishGroups(id)
      setGroups(response || [])
    } catch (err) {
      console.error('Failed to fetch groups:', err)
      setError(err.message || 'Failed to load groups')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchGroups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const handleFormChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    if (formError) setFormError(null)
  }

  const resetForm = () => {
    setForm({ name: '', description: '' })
    setFormError(null)
    setShowCreateForm(false)
  }

  const handleCreateGroup = async () => {
    if (!parishId) return
    if (!form.name.trim()) {
      setFormError('Group name is required')
      return
    }

    try {
      setCreating(true)
      setFormError(null)
      const payload = { name: form.name.trim() }
      if (form.description.trim()) payload.description = form.description.trim()

      const newGroup = await api.createGroup(parishId, payload)
      resetForm()
      navigate(`/app/parish/groups/${newGroup.id}`)
    } catch (err) {
      console.error('Failed to create group:', err)
      setFormError(err.message || 'Failed to create group')
    } finally {
      setCreating(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded p-6">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold font-display text-charcoal">Groups</h1>
          <p className="text-sm text-charcoal-muted mt-1">
            Private groups inside your parish. Only members can see a group or its announcements.
          </p>
        </div>
        {!showCreateForm && (
          <Button
            onClick={() => setShowCreateForm(true)}
            className="bg-gold text-black hover:bg-gold/90 flex-shrink-0"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Group
          </Button>
        )}
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="border border-gold/30 rounded p-4 mb-6 space-y-4 bg-surface-linen">
          <h2 className="text-base font-semibold text-charcoal">New Group</h2>
          {formError && (
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <p className="text-red-600 text-sm">{formError}</p>
            </div>
          )}
          <div>
            <Label htmlFor="group-name" className="text-charcoal text-xs">Name *</Label>
            <Input
              id="group-name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleFormChange}
              placeholder="e.g. Finance Committee"
              maxLength={200}
              className="mt-1 bg-white border-surface-border text-charcoal"
            />
            <p className="mt-1 text-xs text-charcoal-muted">{form.name.length}/200 characters</p>
          </div>
          <div>
            <Label htmlFor="group-description" className="text-charcoal text-xs">Description</Label>
            <textarea
              id="group-description"
              name="description"
              value={form.description}
              onChange={handleFormChange}
              rows={3}
              maxLength={1000}
              className="mt-1 w-full px-3 py-2 bg-white border border-surface-border rounded text-charcoal placeholder-charcoal-faint focus:outline-none focus:ring-2 focus:ring-gold/50"
              placeholder="What is this group for?"
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-surface-border">
            <Button type="button" variant="ghost" onClick={resetForm} disabled={creating}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleCreateGroup}
              disabled={creating}
              className="bg-gold text-black hover:bg-gold/90"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Group'
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Groups List */}
      {groups.length === 0 ? (
        <div className="bg-white border border-surface-border rounded p-8 text-center">
          <Users className="w-12 h-12 text-charcoal-faint mx-auto mb-3" />
          <p className="text-charcoal-muted">
            No groups yet. Create your first group to share announcements with a specific set of people.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => (
            <div
              key={group.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/app/parish/groups/${group.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  navigate(`/app/parish/groups/${group.id}`)
                }
              }}
              className="bg-white border border-surface-border rounded p-4 cursor-pointer hover:bg-surface-linen/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-gold flex-shrink-0" />
                    <h3 className="text-base font-semibold text-charcoal truncate">{group.name}</h3>
                  </div>
                  {group.description && (
                    <p className="text-sm text-charcoal-muted mt-1 line-clamp-2">{group.description}</p>
                  )}
                </div>
                <span className="text-xs text-charcoal-muted flex-shrink-0">
                  Created {formatDate(group.createdAt)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
